import { Distribute, Icon, SvgComment, SvgSmileAlt, Text } from '@loomhq/lens';
import classnames from 'classnames';
import moment from 'moment';
import React from 'react';
import { useDispatch } from 'react-redux';

import { requestVideoPlayerSeekToTs } from 'actions/videoPlayer';
import { CLOUDFRONT_URI } from 'constants/routes';
import { ANONYMOUS_NAME } from 'constants/userAppSettings';
import { secondsToVideoTS } from 'utilities/time';

import { toPercentOfDuration } from './utils';

import { emojiTypeToPath } from 'components/ui-components/emoji';
import UserAvatar from 'components/user-avatar';

import styles from './styles.module.less';

export const Reaction = ({ type, ts, content, onClick, videoDuration }) => {
  const ref = React.useRef();
  const className = type + ' opaque';
  const isCommentType = type === 'comment';

  const style = { left: toPercentOfDuration(ts, videoDuration) };

  React.useLayoutEffect(() => {
    const el = ref?.current;
    const parentRect = el.parentElement?.getBoundingClientRect();
    const childRect = el.getBoundingClientRect();
    const pushedOut = childRect.right - parentRect.right;

    if (pushedOut > 0) {
      // if emoji is partially or fully out of the container box
      // push it back in
      el.style.transform = `translateX(${-1 * pushedOut}px)`;
    }
  });

  let title = '';
  const path = emojiTypeToPath[content];

  if (isCommentType) {
    title = content;
  }

  return (
    <div
      className={className}
      style={style}
      title={title}
      onClick={onClick}
      ref={ref}
    >
      {!path ? null : <img src={path} />}
    </div>
  );
};

export const ViewerInfo = ({
  user = { avatar: null, name: null },
  events = [],
}) => {
  const comments = events.filter(evt =>
    ['comment', 'reply'].includes(evt.type)
  );
  const reactions = events.filter(evt => evt.type === 'reaction');

  return (
    <article className={styles.viewerInfo}>
      <ViewerAvatar avatar={user.avatar} name={user.name} />
      <div>
        <ViewerName {...user} />
        <Distribute gap="medium" alignment="center">
          <ViewerEventCounter type="comment" items={comments || []} />
          <ViewerEventCounter type="reaction" items={reactions || []} />
        </Distribute>
      </div>
    </article>
  );
};

const ViewerEventCounter = ({ type, items = [] }) => {
  const tot = items.length;
  const Ico = type === 'comment' ? SvgComment : SvgSmileAlt;

  return (
    <Distribute alignment="center" gap="xsmall">
      <Icon icon={<Ico />} size={2} color="bodyDimmed" />
      <Text color="bodyDimmed">{tot}</Text>
    </Distribute>
  );
};

export const ViewerAvatar = ({ avatar, size = 4, name }) => {
  const imageSrc = avatar && `${CLOUDFRONT_URI}/${avatar}`;
  const nameProp = name;
  const avatarName = nameProp !== ANONYMOUS_NAME && nameProp;

  return (
    <UserAvatar
      avatarSize={size}
      avatarSrc={imageSrc}
      className={!imageSrc && !avatarName ? 'test-anonAvatar' : ''}
      name={avatarName}
    />
  );
};

export const ViewerName = props => (
  <div>
    <Text fontWeight="medium" htmlTag="h3">
      {props.name}
    </Text>
  </div>
);

export const ViewsList = props => {
  return (
    <>
      {props.views.map((ts, i) => {
        const timestamp = Number(ts);
        const date = moment(timestamp).format('MMMM Do, YYYY h:mma');

        return (
          <Text variant="body" key={i}>
            <time>{date}</time>
          </Text>
        );
      })}
    </>
  );
};

export const EventsList = ({ events = [] }) => {
  const dispatch = useDispatch();
  const actions = {
    comment: 'Commented at',
    reaction: 'Reacted at',
    reply: 'Replied at',
  };

  if (!events.length) {
    return null;
  }

  const playVideoAt = ts => () => {
    dispatch(requestVideoPlayerSeekToTs(ts));
  };

  return (
    <div className="px:small py:medium bgc:backgroundSecondary radius:medium">
      <div className="px:small mb:small">
        <Text fontWeight="medium">Activity</Text>
      </div>
      {events.sort(sortCompareByTimestamp).map((evt, i) => {
        const action = actions[evt.type];
        const path = emojiTypeToPath[evt.content];

        return (
          <div
            key={i}
            className={classnames(styles.eventItem, styles.isClickable)}
            onClick={playVideoAt(evt.ts)}
          >
            {path && (
              <img src={path} alt={`${action} ${secondsToVideoTS(evt.ts)}`} />
            )}
            {action}
            <time>{secondsToVideoTS(evt.ts)}</time>
          </div>
        );
      })}
    </div>
  );
};

function sortCompareByTimestamp({ ts }, { ts: ts2 }) {
  if (ts > ts2) {
    return 1;
  }

  if (ts2 > ts) {
    return -1;
  }

  return 0;
}
